import { _js } from '@ifixit/localize';
import { Autocompleter } from 'Shared/Autocompleter/Autocompleter';
import { DeviceFinder } from 'Guide/device_finder';
import { Utils } from 'Shared/utils';
import { Auth } from 'Shared/auth';
import { Modal } from 'Shared/modal';
import { MediaTarget } from 'Shared/FrameModules/MediaLibrary/target';
import { FormManager } from 'Shared/form_manager';
import { WikiTextEditor } from 'Shared/wiki_text_editor';
import EditorCache from 'Shared/prosemirror/editorcache';
import { CookieUtils } from 'Shared/cookies/cookie-utils';
import { CategoryCard } from 'Guide/category_card';
import {
   clearSessionStorage,
   getSerializedText,
   getEditor,
} from 'Shared/prosemirror/tiptap/editor_cache';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import {
   onSelectedWikiDeviceChange,
   getSelectedWikiDevice,
   setSelectedWikiDevice,
   setSavingState,
} from 'Shared/ifixit_store';
import Validation from 'Shared/validation';
import { safeSessionStorage } from '@ifixit/utils';
import 'Guide/wiki_search_new_guide';
import legacyEditorHandle from 'Guide/legacy_editor_handle';

onDomReady(() => {
   let handle = false;
   const elementAbovePM = document.querySelector('#guideVideoUpload');
   const guideIntro = GuideIntro.initialize();

   if (elementAbovePM) {
      const showProseMirror = async function (entries) {
         const isInView = entries[0].target === elementAbovePM && entries[0].isIntersecting;

         if (isInView && !handle) {
            await getEditor();
            handle = true;
         }
      };
      const observer = new IntersectionObserver(showProseMirror);
      observer.observe(elementAbovePM);
   }

   async function getEditor() {
      const prosemirror = document.querySelector('.prosemirror');
      if (!prosemirror) {
         guideIntroLegacyHandle('introIntroduction');
         return;
      }
      const { getOrCreateHandle } = await import(
         /* webpackPrefetch: true */ 'Shared/prosemirror/guide_intro.jsx'
      );
      return getOrCreateHandle(
         'introIntroduction',
         '#introductionDiv',
         guideIntro.handleIntroModified.bind(guideIntro)
      );
   }

   if (window.TagEditor) {
      TagEditor.initialize();
   }

   if (window.AuthorEditor) {
      AuthorEditor.initialize();
   }
});

function guideIntroLegacyHandle(id) {
   EditorCache.set(id, legacyEditorHandle('introIntroduction'));
   // Set up the toolbar on the introduction box.
   const items = ['heading', 'bold', 'italic', 'ul', 'ol', 'link', 'video'];
   new WikiTextEditor(id, null, {
      toolbarItems: items,
      className: 'wikiTextEditorToolbar',
      helpLink: window.guide_constants.GuideURI('TAG_FORMATTING_HELP'),
      helpText: _js('Formatting help'),
      defaultOpenInNewTab: true,
   });
   EditorCache.get(id).editable.addClass('hasTip');
}

const GuideIntro = {
   initialize: function () {
      this.formid = 'guideIntroForm';
      this.saved = false;
      this.formModified = false;
      this.introModified = false;
      this.introForm = $(this.formid);
      this.introManager = new FormManager(this.introForm);

      // Form fields we augment.
      this.introType = $('introType');

      // If the site has fewer than 25 categories, we will use the
      // old topic chooser and need the phtml elements related to that.
      // If the site has more, we are using our new react component
      // for device selection.
      if (App.siteHasFewCategories) {
         this.introDevice = $('introDevice');
      }

      this.introThing = $('introThing');
      this.introTitle = $('introTitle');
      this.summary = $('introIntroduction');
      this.videoInput = $('guideVideoUpload') ? $('guideVideoUpload').getFirst('input') : null;
      this.showMore = $('showMore');
      this.flagInput = $('flagInput');

      FlagEditor.initialize(this.flagInput);

      // Clearly we have JS.
      $('noJSFlag').set('value', 'false');

      // Use old topicChooser if fewer than 25 categories on site
      if (App.siteHasFewCategories) {
         TopicChooser.initialize(this.introManager);
      }
      // For new wiki search, check if we already have a chosen device,
      // and make a category card if we do.
      // For the new wiki search, we are using redux to store info about
      // the wiki device.
      else if (getSelectedWikiDevice()) {
         const wikiSearch = document.querySelector('[data-name="NewGuideWikiSearch"]');
         let catCard = new CategoryCard(wikiSearch);
         catCard.setDeviceInfo(getSelectedWikiDevice());
         catCard.toggleDeviceBlurb(true);
      }

      when($('mainGuideImage'), mainGuideImage => {
         new MediaTarget.createFromElement(mainGuideImage);
      });

      when($('guideVideoUpload'), videoUpload => {
         // eslint-disable-next-line no-var
         var videoTarget = MediaTarget.createFromElement(videoUpload, {
            iconClass: 'addVideo',
            customAttach: function (mediaItem, finished) {
               if (mediaItem && mediaItem.data.filter_state() !== true) {
                  alert(videoTarget.getErrorMessage(mediaItem));
                  return finished(mediaItem, false);
               }

               let oldTarget = this._immediatelyAttach(mediaItem);
               if (finished) {
                  return finished(oldTarget, true);
               }
            },
         });
      });

      // Set up "form modified" behavior.
      this.inputWatchId = 'formInputs';
      this.formWatchInputs = $(this.inputWatchId);
      this.introManager.addEvent('modifiedChanged', formModified => {
         this.handleModified(formModified);
      });

      // The introduction is hidden for new guides.
      clickSafe(this.showMore, function () {
         $$('.introductionDiv, #flagDiv').toggle();
         $$('#guideVideoUpload').setStyle('display', 'inline-block'); // layout needs inline-block, not block

         let arrow = this.getChildren('i')[0];
         let text = this.getChildren('p')[0];

         arrow.toggleClass('fa-angle-down');
         arrow.toggleClass('fa-angle-up');

         if (arrow.hasClass('fa-angle-up')) {
            text.set('html', _js('Show Less'));
         } else {
            text.set('html', _js('Show More'));
         }
      });

      // Get the title formats for enabled guide types.
      this.typeTitles = {};
      $$('.typeTitle').each(span => {
         this.typeTitles[span.id.replace('Title', '')] = span.get('text');
      });

      // Selecting a type fills in the type name in a few bits of text and hides
      // or shows some fields which are only applicable to a valid type.
      this.introTypeSelect = $('introType');
      if (this.introTypeSelect) {
         this.introTypeSelect.addEvent('change', () => {
            let type = this.introTypeSelect.get('value').toLowerCase();
            let text = this.introTypeSelect.getSelected()[0].get('rel');

            // Hide or show fields that depend on the type.
            $$('.thingDiv').invoke(this.typeTitles[type].match('%thing') ? 'show' : 'hide');

            // Fill in the intro type where it's needed.
            $$('.introTypePrompt').invoke('set', 'text', text);
         });
      }

      // Generate the full guide title from the device and "thing".
      let generateTitle = function () {
         let hasDevice = App.siteHasFewCategories
            ? !this.introDevice.hasClass('defaultText')
            : getSelectedWikiDevice();
         let hasThing = this.introThing && this.introThing.value.trim() !== '';

         if (hasDevice || hasThing) {
            let type = this.introTypeSelect.get('value');
            const getDevice = () => {
               if (hasDevice && App.siteHasFewCategories) {
                  return this.introDevice.get('value');
               }
               if (hasDevice) {
                  return getSelectedWikiDevice();
               }
               return '';
            };
            let thing = hasThing ? this.introThing.get('value') : '';
            return this.typeTitles[type]
               .substitute(
                  {
                     type: type,
                     device: getDevice(),
                     thing: thing,
                  },
                  /%([a-z]+)/g
               )
               .replace(/\s+/g, ' ');
         } else {
            return '';
         }
      }.bind(this);

      // Set a `customized` flag to true if the title differs from the
      // auto-generated title for the current device and thing, and false
      // otherwise. If the title field is empty, then set it to match the
      // auto-generated title, and set customized to false.
      this.customized;
      let checkCustomized = function () {
         let autoTitle = generateTitle();
         let empty = this.introTitle.value === '';
         if (!empty && this.introTitle.value != autoTitle) {
            this.customized = true;
         } else {
            this.customized = false;
            if (empty && App.guideAutoTitle) {
               this.introTitle.value = autoTitle;
            }
         }
      }.bind(this);
      checkCustomized();

      this.renderTitle = function () {
         if (!this.customized && App.guideAutoTitle) {
            this.introTitle.value = generateTitle();
         }
      }.bind(this);

      let inputs = [];
      if (this.introTypeSelect) {
         inputs.push(this.introTypeSelect);
      }
      if (this.introThing) {
         inputs.push(this.introThing);
      }

      if (this.introDevice) {
         inputs.push(this.introDevice);
      }

      inputs.invoke('addEvent', 'change', this.renderTitle);
      inputs.invoke('addEvent', 'keyup', this.renderTitle);
      if (!App.siteHasFewCategories) onSelectedWikiDeviceChange(this.renderTitle);

      this.introManager.setValidator(this.introTitle, () => {
         checkCustomized();
         let value = this.introTitle.get('value').trim();
         if (!value && !this.introTitle.disabled) {
            return this.introManager.requiredError();
         }
      });

      if (this.introThing) {
         this.introManager.setValidator(this.introThing, () => {
            let empty = this.introThing.value.trim() === '';
            if (empty && this.introThing.getParent().getStyle('display') != 'none') {
               return this.introManager.requiredError();
            }
         });
      }

      // Validate the form when the user clicks Save.
      $('save').addEvent('click', ev => {
         ev.stop();
         setSavingState(true);

         // check if device is invalid first (since the new react component won't
         // be seen in form manager))
         if (!App.siteHasFewCategories && !Validation.isValidWikiTitle(getSelectedWikiDevice())) {
            return;
         }
         this.introManager.validate(this.introManager.submit);
      });

      // Before submitting we inject inputs for prerequisites and tools and
      // disable beforeunload warnings.
      this.introManager.addSubmitHandler(() => {
         FlagEditor.injectFlags();

         Auth.required({
            from: $('saveBtn'),
            message: _js('Log in to save a guide'),
            onAuthorize: function () {
               $('save').disabled = true;
               this.saved = true;

               this.saveGuide();
            }.bind(this),
         });
      });

      Utils.UnsavedChanges.addHandler(() => {
         if (!this.saved && this.formWatchInputs.hasClass('unsaved')) {
            return _js('You have unsaved changes. Your changes will be lost if you continue.');
         }
      });

      return this;
   },

   handleModified: function (formModified) {
      this.formModified = formModified;
      // Flags also affect the guide modified state.
      const guideModified = formModified || this.introModified || FlagEditor.isModified();
      this.formWatchInputs.toggleClass('unsaved', guideModified);
      //Disable or enable the save button
      $('save').toggleClass('buttonLinkDisabled', !guideModified);
      $('save').disabled = !guideModified;
   },

   handleIntroModified: function (modified) {
      this.introModified = modified;
      this.handleModified(this.formModified);
   },

   saveGuide: function () {
      let guideValues = this.getFormValues();
      let method = 'POST';
      let path = 'guides';
      let queryParams = null;

      if (App.guideApi['new']) {
         guideValues.langid = App.guideApi.langid;
      } else {
         method = 'PATCH';
         path = 'guides/' + App.guideApi.guideid;
         queryParams = {
            revisionid: App.guideApi.revisionid,
         };
      }

      new Request.API_2_0(path, {
         queryParams: queryParams,
         method: method,
         statusPanelMessage: _js('Saving...'),
         onSuccess: function (response) {
            const firedEventsArray = safeSessionStorage.getJson('firedEvents') || [];
            if (
               safeSessionStorage.getItem('newGuideId') == response.guideid &&
               !firedEventsArray.includes('Introduction')
            ) {
               trackInPiwikAndGA({
                  eventCategory: 'Create a Guide Steps',
                  eventAction: 'Create a Guide Step - "Introduction" - Complete',
               });
               firedEventsArray.push('Introduction');
               safeSessionStorage.setJson('firedEvents', firedEventsArray);
            }
            clearSessionStorage('introIntroduction');

            if (App.guideApi['new']) {
               // Add session storage to track edits to a new guide on the next pages
               safeSessionStorage.setItem('newGuideId', response.guideid);
               safeSessionStorage.setJson('firedEvents', []);
               this.trackGuideCreation(guideValues.type);
               window.location = '/Guide/edit/' + response.guideid + '/new-after/0';
            } else {
               CookieUtils.set('guide_updated', true, {
                  maxAgeInSeconds: 2,
               });
               window.location.reload();
            }
         }.bind(this),
         onFailure: function (xhr, message) {
            $('save').disabled = false;
            this.saved = false;

            if (message === 'category does not exist') {
               $('createCategoryLink').setStyle('display', 'inherit');
            }
         },
      }).send(guideValues);
   },

   // Track the creation of a guide and which type of guide was created
   trackGuideCreation: function (type) {
      let typeString = 'Guide';

      switch (type) {
         case 'teardown': {
            typeString = 'Teardown';
            break;
         }
         case 'replacement': {
            typeString = 'Replacement';
            break;
         }
         case 'disassembly': {
            typeString = 'Disassembly';
            break;
         }
         case 'technique': {
            typeString = 'Technique';
            break;
         }
      }

      const eventAction = `Create a Guide - ${typeString} - Form - Submit`;

      trackInPiwikAndGA({
         eventCategory: 'Create a Guide',
         eventAction: eventAction,
      });
   },

   getFormValues: function () {
      const getIntro = () => getSerializedText('introIntroduction');
      let imageInput = $('introImageid');
      let imageid = imageInput ? imageInput.value.toInt() || null : null;
      return {
         type: this.introType.value,
         category: App.siteHasFewCategories ? this.introDevice.value : getSelectedWikiDevice(),
         subject: this.introThing ? this.introThing.value : '',
         title: this.introTitle.value,
         objectid: this.videoInput ? Number.parseInt(this.videoInput.value, 10) : null,
         introduction: getIntro(),
         flags: FlagEditor.getFlagids(),
         image: imageid,
      };
   },
};

/**
 * Topic Chooser:
 * handles the different UI cases for topic selection (button, drop-down,
 * auto-complete)
 */
const TopicChooser = (function () {
   let buttonLink,
      buttonLinkWrapper,
      button,
      dropdown,
      textbox,
      formManager,
      container,
      modalContents,
      formElement,
      topicModal;

   function setupValidation() {
      formManager.setValidator(formElement, () => {
         let value;
         if (dropdown) {
            value = dropdown.get('value').trim();
            if (value.length < 1) {
               return formManager.requiredError();
            }
         } else if (textbox) {
            if (textbox.hasClass('defaultText')) {
               return formManager.requiredError();
            }
            if (new RegExp(App.invalidDeviceRegex).test(getSelectedTopic())) {
               return 'Device contains invalid characters. The characters # < > [ ] | { } , + ? & / \\ % : ; $ ^ * and tabs are not allowed.';
            }
         }
      });
   }

   function setupButtons() {
      button = button || container.getElement('button.topic');

      if (button) {
         button.addEvent('click', event => {
            event.stop();
            openModal();
         });
      }

      buttonLink = buttonLink || container.getElement('#createCategoryLinkbutton');
      if (buttonLink) {
         buttonLink.addEvent('click', event => {
            event.stop();
            openModal();
         });
      }
   }

   function setupDropDown() {
      dropdown = dropdown || container.getElement('select.topic');

      if (!dropdown) {
         return;
      }

      dropdown.addEvent('change', function (event) {
         if (this.selectedIndex === 0) {
            openModal();
         }
      });
   }

   function setupAutoComplete() {
      textbox = textbox || container.getElement('input.topic');

      // Device finder
      if (!textbox) {
         return;
      }

      const exampleText = _js('Example: 2001-2005 Honda Civic');
      let catCard = new CategoryCard(textbox, exampleText);

      let deviceText = textbox.get('value').trim();
      if (deviceText) {
         catCard.updateDeviceInfo(deviceText);
         catCard.toggleDeviceBlurb(true);
      }

      DeviceFinder.initialize(textbox, {
         onHide: function () {
            catCard.updateDeviceInfo(this.element.value);
         },
         onSelection: function () {
            catCard.toggleDeviceBlurb(true);
         },
      });

      textbox.addEventListener('focus', function () {
         this.fireEvent('click');
      });

      $$('.change-button').addEvent('click', () => {
         $('createCategoryLink').setStyle('display', 'none');
      });

      // Set default text.
      formManager.setDefaultText(textbox, exampleText);
   }

   function openModal() {
      if (Auth.required({})) {
         topicModal.open();
      } else {
         dropdown.selectedIndex = 1;
      }
      buttonLinkWrapper = buttonLinkWrapper || container.getElement('#createCategoryLink');
      if (buttonLinkWrapper) {
         buttonLinkWrapper.hide();
      }
   }

   function setupModal() {
      if (!modalContents) {
         return;
      }

      topicModal = new TopicCreationModal();
      topicModal.addEvent('close', (success, newTitle) => {
         if (success && dropdown) {
            dropdown.removeClass('hidden');
            if (button) {
               button.hide();
            }

            let newOption = Utils.createElements({
               tag: 'option',
               label: newTitle,
               text: newTitle,
            });
            newOption.set('value', newTitle);
            dropdown.grab(newOption);
            dropdown.set('value', newTitle);
            formManager.validate();
         } else if (dropdown) {
            // Failure or Cancel = select second option
            dropdown.selectedIndex = 1;
         } else {
            if (newTitle) {
               GuideIntro.introDevice.set('value', newTitle);
            }
         }
      });

      if (dropdown && dropdown.selectedIndex === 0) {
         openModal();
      }
   }

   function getSelectedTopic() {
      return formElement.get('value').trim();
   }

   return {
      initialize: function (inFormManager) {
         formManager = inFormManager;
         container = $('topicChooser');
         modalContents = $('create_topic');
         formElement = $('introDevice');

         if (container) {
            setupButtons();
            setupDropDown();
            setupAutoComplete();
         }
         setupModal();
         setupValidation();
      },
   };
})();

const TopicCreationModal = new Class({
   Implements: [Events],

   initialize: function () {
      let initialized,
         submitBtn,
         cancelBtn,
         topicTitle,
         topicSummary,
         self = this,
         isClosed = true,
         modalContents;

      function ensureInitialized() {
         if (initialized) {
            return;
         }
         initialized = true;

         submitBtn = $('topicCreateSubmit');
         cancelBtn = $('topicCreateCancel');
         modalContents = $('create_topic');
         topicTitle = $('topicTitle');
         topicSummary = $('topicSummary');

         submitBtn.addEvent('click', event => {
            event.stop();
            save();
         });
         cancelBtn.addEvent('click', event => {
            event.stop();
            close();
         });
      }

      function open() {
         ensureInitialized();
         topicTitle.set('value', $('introDevice').get('value'));
         isClosed = false;
         Modal.open({ type: 'element', element: modalContents, onClose: close });
      }

      function close(success, newTitle) {
         if (isClosed) {
            return;
         }
         isClosed = true;
         Modal.close();
         self.fireEvent('close', [success, newTitle]);
      }

      function save() {
         new Request.AjaxIO('createTopic', {
            onError: function (response) {
               alert(response.error);
            },
            onSuccess: function (response) {
               close(true, response.title);
               GuideIntro.customized = false;
               GuideIntro.renderTitle();
            },
         }).send({
            title: topicTitle.get('value'),
            summary: topicSummary.get('value'),
         });
      }

      this.open = open;
   },
});

/**
 * Handles adding/removing flags from a guide. If there is no flagInput (when
 * making a new guide), then noFlags is set, and the public interface doesn't
 * allow adding/removing flags, and always returns false for isModified.
 */
const FlagEditor = {
   noFlags: false,
   saved: false,

   initialize: function (flagInput) {
      if (!flagInput) {
         this.noFlags = true;
         return;
      }

      this.flagDiv = $('flagDiv');
      this.flagInput = flagInput;
      this.flags = $$('.guideFlag');

      // Add the remove events.
      this.flags.each(div => {
         let a = div.getElement('a.flagRemove');
         a.addEvent('click', ev => {
            ev.stop();
            div.removeClass('hasFlag').hide();
            this.updateTokens();
            GuideIntro.handleModified(GuideIntro.formModified);
         });
      });

      // Setup the autocompleter.
      this.autocompleter = new Autocompleter.Local(this.flagInput, [], {
         className: 'autocompleter-choices flags',
         postVar: 'search',
         autoTrim: true,
         zIndex: 50_001, // 1 higher than the image drawer
         overflow: true,
         filterSubset: true,
         maxChoices: 100,
         onSelection: function (choice, finish, c) {
            if (!finish) {
               return;
            }

            if (choice) {
               this.addFlag(choice.get('text'));
            }
         }.bind(this),
      });

      this.updateTokens();

      // Add the 'enter' event.
      this.flagInput.addEvents({
         focus: this.autocompleter.prefetch.bind(this.autocompleter),
         keypress: function (ev) {
            if (ev.key == 'enter') {
               ev.stop();
               this.autocompleter.hideChoices();
               this.addFlag();
            }
         }.bind(this),
      });
   },

   addFlag: function (value) {
      value = value ? value : this.flagInput.value;
      value = value.toLowerCase();

      this.flags.each(div => {
         let title = div.getElement('h4');
         if (title.get('text').toLowerCase() == value) {
            this.flagInput.value = '';
            div.addClass('hasFlag').show();
         }
      });

      this.flagInput.blur();
      this.updateTokens();
      GuideIntro.handleModified(GuideIntro.formModified);
   },

   updateTokens: function () {
      let tokens = [];
      let setupSaved = !this.saved;
      if (setupSaved) {
         this.saved = [];
      }

      // Generate the flag list.
      this.flags.each(div => {
         if (!div.hasClass('hasFlag')) {
            tokens.push(div.getElement('h4').get('text'));
         } else if (setupSaved) {
            this.saved.push(div.get('rel'));
         }
      });

      this.autocompleter.tokens = tokens;
      this.autocompleter.cached = tokens;
   },

   injectFlags: function () {
      if (this.noFlags) {
         return;
      }

      $$('.injectedFlag').invoke('destroy');
      $$('.hasFlag').each(div => {
         let flagid = div.get('rel');
         new Element('input', {
            type: 'hidden',
            name: 'intro[flags][]',
            value: flagid,
         })
            .addClass('injectedFlag')
            .inject(this.flagDiv);
      });
   },

   getFlagids: function () {
      return $$('.hasFlag').map(div => div.get('rel'));
   },

   isModified: function () {
      if (this.noFlags) {
         return false;
      }

      let current = [];
      this.flags.each(div => {
         if (div.hasClass('hasFlag')) {
            current.push(div.get('rel'));
         }
      });

      if (current.length != this.saved.length) {
         return true;
      }

      return !this.saved.every(item => current.contains(item));
   },
};
